<template>
  <score-card
    :title="'Table ' + game.race.tableNumber"
    :teams="teamMembers"
    :goals="scoreGoals"
    :hints="hints"
    :max-score-button="maxVals"
    :has-scores-been-submitted="!game.active"
    :active="!!game.active"
    :clickable="clickable"
    :is-creator="isCreator"
    :submitting="submitting"
    :can-destroy="false"
    size="text-title-medium"
    @score="(ti, v) => recordScore(ti, v)"
    @ironman-score="(ti) => recordScore(ti)"
  />
</template>

<script>
import ScoreCard from "@/components/score-card.vue";
import { acquireScoringLock } from "@/utils/scoring-lock";
import { isDecided } from "@/utils/race-utils";
import { isScorecardStale } from "@/utils/scorecard-guard";
import { recalcScorecardsForRace } from "@/utils/recalc-scorecards-for-race";

export default {
  name: "GameCard",
  components: {
    ScoreCard
  },
  props: {
    clickable: {
      type: Boolean,
      default: false
    },
    game: {
      type: Object,
      required: true
    }
  },
  inject: ["$store"],
  data () {
    return {
      submitting: false
    };
  },
  computed: {
    scores () {
      return {
        home: this.game.scores[this.game.race.homeSquad.id],
        away: this.game.scores[this.game.race.awaySquad.id]
      };
    },
    isCreator () {
      return this.game.race.league.userId === this.$store.user.id;
    },
    teamMembers () {
      return this.game.teams || [];
    },
    scoreGoals () {
      return [this.scores.home, this.scores.away];
    },
    hints () {
      return this.game.race.squads.map(squad => {
        const remaining = this.game.race.remainingGoalsBySquadIdExcludingThisGame(this.game)[squad.id];
        return (remaining > 0 && remaining < this.game.race.league.pointsPerGame) ? remaining : null;
      });
    },
    maxVals () {
      return this.game.race.squads.map(squad =>
        this.game.race.maxScoreForSquadInGame(squad, this.game)
      );
    }
  },
  methods: {
    async storeStartOfRaceMuAndSigma () {
      const members = this.game.race.squads.flatMap(squad => squad.members);
      await Promise.all(
        members.map(async member => {
          await member.promiseUserModel;
          member.userModel.scorecard;
          await member.userModel.promiseScorecard;
          return this.game.race.createMemberSnapshot({
            memberId: member.id,
            memberUserId: member.memberUserId,
            startOfRaceMu: member.userModel.scorecard.mu,
            startOfRaceSigma: member.userModel.scorecard.sigma
          });
        })
      );
    },
    async recalcScorecardsForCurrentRace () {
      const members = this.game.race.squads.flatMap(squad => squad.members);
      await Promise.all(
        members.map(async member => {
          await member.promiseUserModel;
          member.userModel.scorecard;
          await member.userModel.promiseScorecard;
        })
      );

      this.game.race.memberSnapshots;
      await this.game.race.promiseMemberSnapshots;

      const memberRatings = recalcScorecardsForRace(this.game.race);

      const updatedAt = new Date();
      await Promise.all(
        members.map(member => {
          const rating = memberRatings[member.id];
          if (rating) {
            return member.userModel.scorecard.immediateUpdate({
              mu: rating.mu,
              sigma: rating.sigma,
              updatedAt
            });
          }
        })
      );

      await this.game.race.immediateUpdate({ ratingsSyncedAt: new Date() });
    },
    async blockedMembers () {
      const members = this.game.race.squads.flatMap(squad => squad.members);
      await Promise.all(
        members.map(async member => {
          await member.promiseUserModel;
          member.userModel.scorecard;
          await member.userModel.promiseScorecard;
        })
      );
      return members.filter(member =>
        isScorecardStale(member.userModel.scorecard.updatedAt, this.game.race.ratingsSyncedAt)
      );
    },
    async recordScore (loserIndex, loserScore) {
      if (this.submitting) return;
      this.submitting = true;

      try {
        await acquireScoringLock(this.game.race.docRef, this.game.race.db, this.game.race.dbFns);
      } catch (lockError) {
        this.submitting = false;
        throw lockError;
      }

      try {
        const winnerIndex = loserIndex === 0 ? 1 : 0;
        const winner = this.game.race.squads[winnerIndex];
        const loser = this.game.race.squads[loserIndex];
        const gameSnapshot = await this.game.race.dbFns.getDoc(this.game.docRef);
        const firstTimeScoreSubmitted = gameSnapshot.data().active;
        if (firstTimeScoreSubmitted && this.game.number === 1) {
          await this.storeStartOfRaceMuAndSigma();
        } else {
          const blocked = await this.blockedMembers();
          if (blocked.length > 0) {
            const proceed = window.confirm(
              `These players have games in another competition that will no longer count toward their rating:\n${blocked.map(member => member.name).join(", ")}\n\n` +
              "To avoid this, don't score multiple tournaments or league races at the same time.\n\n" +
              "Continue submitting?"
            );
            if (!proceed) return;
          }
        }

        const totalsExcludingThisGame = this.game.race.goalsBySquadIdExcludingThisGame(this.game);

        const winnerScore = Math.min(
          this.game.race.league.pointsPerGame,
          this.game.race.league.pointsToWin - totalsExcludingThisGame[winner.id]
        );

        const homeMemberIds = this.game.teams[0].map(member => member.id).filter(Boolean);
        const awayMemberIds = this.game.teams[1].map(member => member.id).filter(Boolean);

        await this.game.immediateUpdate({
          scores: { [winner.id]: winnerScore, [loser.id]: loserScore },
          active: false,
          homeMemberIds,
          awayMemberIds
        });
        const totals = {
          ...totalsExcludingThisGame,
          [winner.id]: totalsExcludingThisGame[winner.id] + winnerScore,
          [loser.id]: totalsExcludingThisGame[loser.id] + loserScore
        };

        await Promise.all([
          this.recalcScorecardsForCurrentRace(),
          firstTimeScoreSubmitted && !isDecided(totals, this.game.race.league.pointsToWin)
            ? this.game.race.createGame({ scores: {}, active: true })
            : undefined
        ]);

        this.expanded = false;
        this.showScore = true;
      } finally {
        await this.game.race.dbFns.updateDoc(this.game.race.docRef, { scoringLock: false });
        this.submitting = false;
      }
    }
  }
};
</script>
